// Вибір навчального року й семестру в топбарі (U04): семестри — з useSemester() F (URL ?semester=, localStorage), групування
// за навчальними роками (підпис — код року з _refs довідника, не UUID), позначка «поточний» — з GET /api/contingent/semesters/current
// (правило сервера; null — семестрів немає, це не помилка).
import type { ReactNode } from 'react';
import { semesterLabel, useSemester, type SemesterItem } from '@/app/semester';
import { Select } from '@/ui';
import { useCurrentSemester } from './api';
import { semesterYearCode, semesterYearLabel } from './semesterYear';

/** Підпис семестру у виборі: назва з довідника; рік додається, лише якщо його немає в назві. */
function optionLabel(s: SemesterItem, year: string | null, isCurrent: boolean): string {
  const label = semesterLabel(s);
  const y = year?.replace('-', '/') ?? null;
  const base = !y || !year || label.includes(y) || label.includes(year) ? label : `${y} · ${label}`;
  return isCurrent ? `${base} — поточний` : base;
}

export interface SemesterPickerProps {
  /** Елемент F на випадок, коли позначку поточного семестру отримати не вдалося (помилка API). */
  fallback?: ReactNode;
}

export function SemesterPicker({ fallback }: SemesterPickerProps) {
  const { semesters, semester, setSemester, isLoading, error } = useSemester();
  const current = useCurrentSemester();
  if (current.error && fallback) return <>{fallback}</>;
  if (error || (!isLoading && !semesters.length)) {
    return (
      <span
        className="hidden truncate rounded-field border border-dashed border-border px-3 py-1.5 text-[13px] text-muted sm:inline"
        title="Створіть навчальний рік і семестр у довідниках «Навчальні роки» і «Семестри»"
      >
        Семестр не задано
      </span>
    );
  }
  const currentId = current.data?.semester.id ?? null;
  const years = new Map<string, SemesterItem[]>();
  for (const s of semesters) years.set(s.academicYearId, [...(years.get(s.academicYearId) ?? []), s]);
  const groups = [...years.values()].map((list) => {
    const year = semesterYearCode(list[0]);
    return {
      label: semesterYearLabel(list[0]),
      options: [...list].sort((a, b) => a.number - b.number).map((s) => ({ value: s.id, label: optionLabel(s, year, s.id === currentId) })),
    };
  });
  const isOther = !!currentId && !!semester && semester.id !== currentId && semesters.some((s) => s.id === currentId);
  return (
    <div className="flex items-center gap-1">
      <Select
        size="sm"
        aria-label="Навчальний рік і семестр"
        className="num w-[8.5rem] truncate sm:w-64"
        value={semester?.id ?? ''}
        disabled={isLoading}
        onChange={(e) => setSemester(e.target.value)}
        groups={groups}
        placeholder={isLoading ? 'Завантаження…' : undefined}
        title={isOther ? 'Вибрано не поточний семестр' : undefined}
      />
      {isOther ? (
        <button
          type="button"
          className="hidden rounded-field px-2 py-1 text-[12px] font-semibold text-primary hover:bg-primary-soft md:inline"
          onClick={() => currentId && setSemester(currentId)}
          title="Перейти до поточного семестру"
        >
          Поточний
        </button>
      ) : null}
    </div>
  );
}
